import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { ListingPageProps } from '@/components/marketplace/listing-view'

import { getListingBySlug, getRelatedListings } from '@/lib/marketplace/listings'
import { getMemberSession } from '@/lib/auth/guards'
import { getSellerByTelegramId } from '@/lib/marketplace/sellers'
import { getAnnouncement, getLegal } from '@/lib/db/settings'
import { getHeaderState } from '@/lib/community/owner-hub'
import { getBaseUrl } from '@/lib/marketplace/url'
import { formatDate, formatMonthYear, relativeTime } from '@/lib/marketplace/format'
import { resolvePublicCommunityContext } from '@/lib/community/context'
import { isStorefrontLive, storefrontBasePath } from '@/lib/community/storefront'

import { ListingView } from '@/components/marketplace/listing-view'

/**
 * One listing on a NON-founding marketplace's storefront.
 *
 * Exactly the same page as /listing/[slug], scoped to the tenant named by the
 * route segment. The three things the view cannot work out for itself travel as
 * props, because they are decided on the server from the resolved context and
 * must survive hydration unchanged.
 */
interface TenantListingPageProps extends ListingPageProps {
    basePath: string
    marketplaceName: string
    noindex: boolean
}

const TenantListingPage: FC<TenantListingPageProps> = ({
    basePath,
    marketplaceName,
    noindex,
    ...props
}): ReactNode => {
    return (
        <ListingView
            {...props}
            basePath={basePath}
            marketplaceName={marketplaceName}
            noindex={noindex}
        />
    )
}

export const getServerSideProps: GetServerSideProps<TenantListingPageProps> = async ({ req, params }) => {
    const communitySlug = typeof params?.communitySlug === 'string' ? params.communitySlug : ''
    const slug = typeof params?.slug === 'string' ? params.slug : ''

    if (!communitySlug || !slug) return { notFound: true }

    /**
     * The tenant comes from the ROUTE SEGMENT and is resolved against the
     * database here, exactly once. Nothing downstream accepts a community id
     * from the request, and every read below is given the id this resolver
     * returned.
     */
    const context = await resolvePublicCommunityContext(communitySlug)

    if (!context) return { notFound: true }

    // A marketplace that is not approved, has its storefront switched off, or is
    // private has no public presence at all. A 404 rather than a message, so an
    // address under review does not confirm that it exists.
    if (!isStorefrontLive(context)) return { notFound: true }

    /*
     * No exception for the founding marketplace. This IS the canonical address
     * for every marketplace's listings now; the top-level /listing/<slug> is
     * the legacy address and redirects here, not the other way round.
     */

    const listing = await getListingBySlug(slug, context.communityId)

    // Unknown slug, or a listing that never reached the public marketplace,
    // renders the real 404 page. No client redirect, no flash.
    if (!listing) return { notFound: true }

    /**
     * Owner preview.
     *
     * A listing that has not reached the marketplace is a 404 for everyone
     * except the seller who owns it. Ownership is decided here, on the server,
     * by resolving the seller from the VERIFIED Telegram id on the session and
     * comparing it to the listing's own `sellerId`. Nothing in the request
     * says who the owner is, so this cannot be spoofed, and a stranger gets
     * the same 404 as a slug that does not exist.
     */
    const hidden = ['draft', 'imported_draft', 'pending_review'].includes(listing.status)

    let ownerPreview = false

    if (hidden) {
        const session = await getMemberSession(req)
        const seller = session?.telegramId
            ? await getSellerByTelegramId(session.telegramId, context.communityId)
            : null

        if (!seller || seller.id !== listing.sellerId) return { notFound: true }

        ownerPreview = true
    }

    if (['suspended', 'banned'].includes(listing.seller.sellerStatus)) return { notFound: true }

    const [related, legal, announcement, header] = await Promise.all([
        getRelatedListings(
            {
                id: listing.id,
                categoryId: listing.categoryId,
                sellerId: listing.sellerId,
                listingType: listing.listingType,
                price: listing.price,
                location: listing.location
            },
            4,
            context.communityId
        ),
        getLegal(context.communityId),
        getAnnouncement(context.communityId),
        getHeaderState(req)
    ])

    const now = new Date()

    return {
        props: {
            listing,
            related,
            disclosure: legal.notAPartyStatement,
            announcement,
            baseUrl: getBaseUrl(req),
            postedOn: formatDate(listing.publishedAt ?? listing.createdAt),
            sellerSince: formatMonthYear(listing.seller.createdAt),
            lastUpdated: relativeTime(listing.bumpedAt ?? listing.publishedAt ?? listing.createdAt, now),
            ownerPreview,
            header,
            basePath: storefrontBasePath(context),
            marketplaceName: context.name,
            // `unlisted` means reachable by direct link but absent from search.
            // The storefront route is the only place that can enforce the second
            // half of that, so it is enforced here.
            noindex: context.visibility === 'unlisted'
        }
    }
}

export default TenantListingPage
